import {customElement, property, queryAssignedElements} from 'lit/decorators.js';
import { style } from '@/components/radio-group/radio-group.styles';
import {html, LitElement} from "lit";
import {labelStyles} from "@/components/label/label.styles";
import {FormValidationMixin} from "@/components/mixins/form-validation-mixin";
import {SbkLabelMixin} from "@/components/label/label-mixin";
import {when} from "lit/directives/when.js";
import {
    RadioButtonValidator,
    MinimumWageValidator,
    NextCareerMoveWstValidator,
    Validator, BankDetailsMmsRadioValidator
} from "@/components/validators/validators";
import {FormControlDependencyMixin} from "@/components/form-control-dependency/form-control-dependency-mixin";
import {SbkRadio} from "@/components/radio/radio";
import {unsafeHTML} from "lit/directives/unsafe-html.js";

/**
 * An example element.
 *
 * @slot - This element has a slot
 */
@customElement('sbk-radio-group')
export class SbkRadioGroup extends FormControlDependencyMixin(FormValidationMixin(SbkLabelMixin(LitElement))) {

    static shadowRootOptions = { ...LitElement.shadowRootOptions, delegatesFocus: true };

    @property()
    description: string = ''

    @property({reflect: true})
    name: string = ''

    @property({type: Boolean})
    disabled: boolean = false

    @property()
    direction: 'horizontal' | 'vertical' = 'vertical'

    @queryAssignedElements({selector: 'sbk-radio'})
    _radioButtons!: Array<SbkRadio>;

    validatorMapper: {
        [key: string]: Validator
    } = {
        'required': RadioButtonValidator,
        'validateMinimumWage': MinimumWageValidator,
        'validateRadioButtons': RadioButtonValidator,
        'validateNextCareerMoveWst': NextCareerMoveWstValidator,
        'validateBankdetailsmmsRadio': BankDetailsMmsRadioValidator,
    }


    constructor() {
        super();
        this.addEventListener('form-control-dependency-show', () => {
            this.show();
            this.setValue();
            this.triggerFormControlDependencyChangeEvent();
        });
        this.addEventListener('form-control-dependency-hide', () => {
            this.hide();
            this.resetValue();
            this.triggerFormControlDependencyChangeEvent();
        });
        this.addEventListener('form-control-dependency-required', () => {
            this._required();
        });
        this.addEventListener('form-control-dependency-optional', () => {
            this.optional();
        });
        this.addEventListener('keydown', this._onKeyDown);
    }

    async connectedCallback() {
        super.connectedCallback();
        if (!this.hasAttribute('role')) {
            this.setAttribute('role', 'radiogroup');
        }
        await this.updateComplete;
        const firstCheckedButton = this._checkedRadioButton;
        this._uncheckAll();
        if (firstCheckedButton) {
            this._checkNode(firstCheckedButton);
        } else if (this._firstRadioButton) {
            this._firstRadioButton.tabIndex = 0;
        }
    }

    static get styles() {
        return [
            style,
            labelStyles
        ];
    }

    render() {
        return html`
            <fieldset @radio-change=${this._checkValidity}
                      class="radio-group--${this.direction} radio-group${this.invalid ? ' input__wrapper--error' : ''}${this.disabled ? ' input__wrapper--disabled' : ''}"
                      aria-describedby="${this.id}-annotation" aria-labelledby="groupLabel">
                ${this.labelTemplate("radio-group", true, this.invalid)}
                ${when(this.description,
                    () => html`
                            <sbk-annotation id="annotation" variant="info">${unsafeHTML(this.description)}</sbk-annotation>
                        `)}
                <slot class="radio-slot"></slot>
                ${this.invalid ? this.validationErrorTemplate(`${this.id}-annotation`) : ""}
            </fieldset>
        `;
    }

    hide(): void {
        this.hidden = true;
        this.invalid = false;
    }

    show(): void {
        this.hidden = false;
    }

    resetValue(): void {
        this._radioButtons.forEach(radioButton => {
            radioButton.resetValue();
        });
    }

    setValue(): void {
        this._radioButtons.forEach(radioButton => {
            radioButton.setValue();
        });
    }

    async _checkValidity(e: Event) {
        const radioButton = <SbkRadio> e.target;
        this._setChecked(radioButton);
        const validators = this.getValidators();
        this.handleErrors(validators, '');
    }

    _onKeyDown(e: KeyboardEvent) {
        const { key } = e;
        switch (key) {
            case 'ArrowUp':
            case 'ArrowLeft':
                e.preventDefault();
                this._setCheckedToPrevButton();
                break;
            case 'ArrowDown':
            case 'ArrowRight':
                e.preventDefault();
                this._setCheckedToNextButton();
                break;
            case 'Home':
                e.preventDefault();
                this._firstRadioButton && this._setChecked(this._firstRadioButton);
                break;
            case 'End':
                e.preventDefault();
                this._lastRadioButton && this._setChecked(this._lastRadioButton);
                break;
            case ' ': {
                e.preventDefault();
                const radioButton = <SbkRadio> e.target;
                this._setChecked(radioButton);
                break;
            }
            default:
                break;
        }
    }

    get _checkedRadioButton(): SbkRadio | undefined {
        const slot: HTMLSlotElement|null = this.renderRoot.querySelector('slot.radio-slot');
        if (slot) {
            return <SbkRadio> slot.assignedElements().find((node) => node.matches('sbk-radio[checked]'));
        }
    }

    get _firstRadioButton(): SbkRadio | undefined {
        const slot: HTMLSlotElement|null = this.renderRoot.querySelector('slot.radio-slot');
        if (slot) {
            const elements = slot.assignedElements().filter((node) => node.matches('sbk-radio[role="radio"]:not([disabled])'));
            return <SbkRadio> elements[0];
        }
    }

    get _lastRadioButton(): SbkRadio | undefined {
        const slot: HTMLSlotElement|null = this.renderRoot.querySelector('slot.radio-slot');
        if (slot) {
            const elements = slot.assignedElements().filter((node) => node.matches('sbk-radio[role="radio"]:not([disabled])'));
            return <SbkRadio> elements[elements.length - 1];
        }
    }

    _prevRadioButton(node: SbkRadio): SbkRadio | null {
        let prev = node.previousElementSibling;
        while (prev) {
            if (prev.getAttribute('role') === 'radio' && !prev.hasAttribute('disabled')) {
                return <SbkRadio> prev;
            }
            prev = prev.previousElementSibling;
        }
        return null;
    }

    _nextRadioButton(node: SbkRadio): SbkRadio | null {
        let next = node.nextElementSibling;
        while (next) {
            if (next.getAttribute('role') === 'radio' && !next.hasAttribute('disabled')) {
                return <SbkRadio> next;
            }
            next = next.nextElementSibling;
        }
        return null;
    }

    _setCheckedToPrevButton() {
        const checkedButton = this._checkedRadioButton || this._firstRadioButton;
        if (checkedButton === this._firstRadioButton) {
            this._lastRadioButton && this._setChecked(this._lastRadioButton);
        } else {
            checkedButton && this._setChecked(this._prevRadioButton(checkedButton));
        }
    }

    _setCheckedToNextButton() {
        const checkedButton = this._checkedRadioButton ?? this._firstRadioButton;
        if (checkedButton === this._lastRadioButton) {
            this._firstRadioButton && this._setChecked(this._firstRadioButton);
        } else {
            checkedButton && this._setChecked(this._nextRadioButton(checkedButton));
        }
    }

    async _setChecked(node: SbkRadio | null) {
        if (!node) {
            return;
        }
        this._uncheckAll();
        this._checkNode(node);
        this._focusNode(node);
        await node.updateComplete
        const inputChangeEvent = new CustomEvent('radio-group-change', {
            bubbles: true
        });
        node.dispatchEvent(inputChangeEvent);
    }

    _uncheckAll() {
        this._radioButtons.forEach(radioButton => {
            radioButton.checked = false;
            radioButton.tabIndex = -1;
        });
    }

    _checkNode(node: SbkRadio) {
        node.checked = true;
        node.tabIndex = 0;
    }

    _focusNode(node: SbkRadio) {
        node.focus();
    }

    triggerFormControlDependencyChangeEvent() {
        this._radioButtons.forEach(radioButton => {
            radioButton.checked = false;
            radioButton.invalid = false;
            const formControlDependencyChangeEvent = new CustomEvent('form-control-dependency-change', {
                bubbles: true
            });
            radioButton.dispatchEvent(formControlDependencyChangeEvent);
        });
    }

    _required(): void {
        this.required = true;
        this.setAttribute('required', 'required');
    }

    optional(): void {
        this.required = false;
        this.removeAttribute('required');
        this.invalid = false;
    }
}
